import { useMemo, useSyncExternalStore } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { AccountUtils } from './AccountUtils';
import type { BillingPlanType } from '@/lib/types/Billing';

export interface AccountPermissions {
  canViewBilling: boolean;
  canViewUsage: boolean;
  canViewTeam: boolean;
  canViewSubAccounts: boolean;
  canViewUploadProfiles: boolean;
  canViewTrialUpgrade: boolean;
  isTrial: boolean;
  isAdmin: boolean;
  isFromParentAccount: boolean;
  isMasterAccountSelected: boolean;
  isLoading: boolean;
  firstAllowedRoute: string | null;
}

/**
 * Reactive billing plan type store — set by useBilling hook when plan data loads.
 * Uses useSyncExternalStore so that changes trigger re-renders in consumers.
 * Cleared on logout to prevent stale data across sessions.
 */
let cachedBillingPlanType: BillingPlanType | null = null;
const listeners = new Set<() => void>();

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}

function getSnapshot(): BillingPlanType | null {
  return cachedBillingPlanType;
}

export function setAccountBillingPlanType(planType: BillingPlanType | null): void {
  if (cachedBillingPlanType === planType) return;
  cachedBillingPlanType = planType;
  listeners.forEach(l => l());
}

export function clearAccountBillingPlanType(): void {
  if (cachedBillingPlanType === null) return;
  cachedBillingPlanType = null;
  listeners.forEach(l => l());
}

/**
 * Derives which account sections the current user can access.
 * Permission gates match the legacy AccountLeftNav.component.tsx exactly.
 */
export function useAccountPermissions(): AccountPermissions {
  const { authData, customerInfo, selectedCustomer, isLoading: isAuthLoading } = useAuth();
  const billingPlanType = useSyncExternalStore(subscribe, getSnapshot);

  return useMemo(() => {
    // Still loading auth data — return loading state, don't redirect
    if (!authData || !customerInfo || !selectedCustomer) {
      return {
        canViewBilling: false,
        canViewUsage: false,
        canViewTeam: false,
        canViewSubAccounts: false,
        canViewUploadProfiles: false,
        canViewTrialUpgrade: false,
        isTrial: false,
        isAdmin: false,
        isFromParentAccount: false,
        isMasterAccountSelected: false,
        isLoading: isAuthLoading || !authData || !customerInfo || !selectedCustomer,
        firstAllowedRoute: null,
      };
    }

    const perms = authData.cpuser.permissions;
    const isAdmin = authData.cpuser.core.isAdmin;
    const isFromParentAccount = AccountUtils.isFromParentAccount(authData, customerInfo);
    // Legacy used !parentCId (falsy check) — backend may return 0 instead of null for master accounts
    const isMasterAccountSelected = !selectedCustomer.cfg.parentCId;
    const isTrial = billingPlanType === 'trial';

    const quota = selectedCustomer.cfg.quota;

    // Permission gates — exact match of legacy AccountLeftNav conditions
    const canViewBilling =
      perms.billingRead &&
      perms.billingWrite &&
      isFromParentAccount &&
      isMasterAccountSelected &&
      !isTrial;

    const canViewUsage =
      perms.billingRead &&
      perms.billingWrite &&
      isFromParentAccount;

    const canViewTeam =
      perms.userRead &&
      perms.userWrite;

    const canViewSubAccounts =
      quota.limits.subaccountLimit !== 0 &&
      perms.subcustomerRead &&
      perms.subcustomerWrite &&
      isFromParentAccount &&
      isMasterAccountSelected;

    const canViewUploadProfiles =
      quota.features.account.allowUploadProfile &&
      perms.serviceRead &&
      perms.serviceWrite;

    const canViewTrialUpgrade = isTrial;

    // Determine first allowed route for /account redirect
    const routeChecks: [boolean, string][] = [
      [canViewBilling, '/account/billing'],
      [canViewUsage, '/account/usage'],
      [canViewTrialUpgrade, '/account/upgrade-trial'],
      [canViewTeam, '/account/team'],
      [canViewSubAccounts, '/account/sub-accounts'],
      [canViewUploadProfiles, '/account/upload-profiles'],
    ];

    const firstAllowedRoute = routeChecks.find(([allowed]) => allowed)?.[1] ?? null;

    return {
      canViewBilling,
      canViewUsage,
      canViewTeam,
      canViewSubAccounts,
      canViewUploadProfiles,
      canViewTrialUpgrade,
      isTrial,
      isAdmin,
      isFromParentAccount,
      isMasterAccountSelected,
      isLoading: false,
      firstAllowedRoute,
    };
  }, [authData, customerInfo, selectedCustomer, billingPlanType, isAuthLoading]);
}
